import React, { useState, useEffect, useCallback } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

function ManageCredentials() {
    const [serviceAccounts, setServiceAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [newApiKey, setNewApiKey] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [createName, setCreateName] = useState('');
    const [createPrefix, setCreatePrefix] = useState('');
    const [createError, setCreateError] = useState('');
    const [nameError, setNameError] = useState('');
    const [creating, setCreating] = useState(false);
    const [showUsageGuide, setShowUsageGuide] = useState(false);

    const getToken = async () => {
        const session = await fetchAuthSession();
        return session.tokens?.idToken?.toString();
    };

    const loadServiceAccounts = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const token = await getToken();
            if (!token) {
                throw new Error('No authentication token available');
            }
            const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/service-accounts`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                if (response.status === 403) {
                    throw new Error('Access denied. Admin privileges required.');
                }
                throw new Error(`Failed to load service accounts (HTTP ${response.status})`);
            }
            const data = await response.json();
            setServiceAccounts(data.service_accounts || []);
        } catch (err) {
            setError(err.message || 'Failed to connect. Check your network.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadServiceAccounts();
    }, [loadServiceAccounts]);

    const handleCreate = async (e) => {
        e.preventDefault();
        setCreateError('');
        setNameError('');
        setNewApiKey(null);
        setCreating(true);
        try {
            const token = await getToken();
            const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/service-accounts`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ name: createName, s3_prefix: createPrefix })
            });
            const data = await response.json();
            if (response.status === 409) {
                setNameError(data.error || 'Service account name already exists');
                return;
            }
            if (!response.ok) {
                setCreateError(data.error || 'Failed to create service account');
                return;
            }
            setNewApiKey(data.api_key);
            setCreateName('');
            setCreatePrefix('');
            setShowCreateForm(false);
            loadServiceAccounts();
        } catch (err) {
            setCreateError(err.message || 'Failed to connect. Check your network.');
        } finally {
            setCreating(false);
        }
    };

    const handleRotateKey = async (accountId) => {
        setError('');
        setNewApiKey(null);
        try {
            const token = await getToken();
            const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/service-accounts/rotate`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ account_id: accountId })
            });
            const data = await response.json();
            if (response.status === 404) {
                setError('Service account not found. It may have been deleted.');
                return;
            }
            if (!response.ok) {
                setError(data.error || 'Failed to rotate key');
                return;
            }
            setNewApiKey(data.api_key);
            loadServiceAccounts();
        } catch (err) {
            setError(err.message || 'Failed to connect. Check your network.');
        }
    };

    const handleDelete = async (accountId, accountName) => {
        if (!window.confirm(`Are you sure you want to delete service account "${accountName}"?`)) {
            return;
        }
        setError('');
        setNewApiKey(null);
        try {
            const token = await getToken();
            const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/service-accounts`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ account_id: accountId })
            });
            const data = await response.json();
            if (response.status === 404) {
                setError('Service account not found. It may have been deleted.');
                return;
            }
            if (!response.ok) {
                setError(data.error || 'Failed to delete service account');
                return;
            }
            loadServiceAccounts();
        } catch (err) {
            setError(err.message || 'Failed to connect. Check your network.');
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    return (
        <div className="admin-section">
            <div className="users-header">
                <h3>Service Accounts</h3>
                <button
                    onClick={() => { setShowCreateForm(!showCreateForm); setNewApiKey(null); }}
                    className="admin-btn"
                >
                    {showCreateForm ? 'Cancel' : 'Create Service Account'}
                </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            {newApiKey && (
                <div className="success-message" style={{ background: '#fff3cd', border: '1px solid #ffc107', padding: '12px', borderRadius: '4px', marginBottom: '16px' }}>
                    <strong>⚠️ Save this API key now — it cannot be retrieved again:</strong>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                        <code style={{ background: '#f8f9fa', padding: '8px 12px', borderRadius: '4px', wordBreak: 'break-all', flex: 1 }}>
                            {newApiKey}
                        </code>
                        <button onClick={() => copyToClipboard(newApiKey)} className="action-btn" title="Copy to clipboard">
                            Copy
                        </button>
                    </div>
                </div>
            )}

            {showCreateForm && (
                <form onSubmit={handleCreate} className="admin-form" style={{ marginBottom: '16px' }}>
                    {createError && <div className="error-message">{createError}</div>}
                    <div className="form-group">
                        <label>Name</label>
                        <input
                            type="text"
                            value={createName}
                            onChange={(e) => { setCreateName(e.target.value); setNameError(''); }}
                            required
                            disabled={creating}
                            placeholder="e.g. scanner-01"
                        />
                        {nameError && <span className="error-message" style={{ fontSize: '0.85em', marginTop: '4px', display: 'block' }}>{nameError}</span>}
                    </div>
                    <div className="form-group">
                        <label>S3 Prefix</label>
                        <input
                            type="text"
                            value={createPrefix}
                            onChange={(e) => setCreatePrefix(e.target.value)}
                            required
                            disabled={creating}
                            placeholder="e.g. forensic-images/scanner-01/"
                        />
                    </div>
                    <button type="submit" className="admin-btn" disabled={creating}>
                        {creating ? 'Creating...' : 'Create'}
                    </button>
                </form>
            )}

            {loading ? (
                <p>Loading service accounts...</p>
            ) : serviceAccounts.length === 0 ? (
                <p>No service accounts found</p>
            ) : (
                <div className="users-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>S3 Prefix</th>
                                <th>Created</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {serviceAccounts.map((account) => (
                                <tr key={account.account_id}>
                                    <td>{account.name}</td>
                                    <td><code>{account.s3_prefix}</code></td>
                                    <td>{new Date(account.created_at).toLocaleDateString()}</td>
                                    <td>
                                        <span className={`status-badge ${account.status === 'active' ? 'enabled' : 'disabled'}`}>
                                            {account.status}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="action-buttons">
                                            <button
                                                onClick={() => handleRotateKey(account.account_id)}
                                                className="action-btn reset"
                                                title="Rotate API Key"
                                            >
                                                Rotate Key
                                            </button>
                                            <button
                                                onClick={() => handleDelete(account.account_id, account.name)}
                                                className="action-btn delete"
                                                title="Delete Service Account"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div style={{ marginTop: '24px', borderTop: '1px solid #dee2e6', paddingTop: '16px' }}>
                <button
                    onClick={() => setShowUsageGuide(!showUsageGuide)}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: '#0066cc',
                        cursor: 'pointer',
                        fontSize: '0.95em',
                        padding: 0,
                        textDecoration: 'underline'
                    }}
                >
                    {showUsageGuide ? '▾ Hide Usage Guide' : '▸ How to use API keys for scripted uploads'}
                </button>

                {showUsageGuide && (
                    <div style={{ marginTop: '12px', background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px', padding: '16px', fontSize: '0.9em', lineHeight: '1.6' }}>
                        <h4 style={{ margin: '0 0 12px 0' }}>Scripted Upload Guide</h4>

                        <p style={{ margin: '0 0 8px 0' }}><strong>Step 1:</strong> Exchange your API key for temporary AWS credentials</p>
                        <pre style={{ background: '#1e1e1e', color: '#d4d4d4', padding: '12px', borderRadius: '4px', overflowX: 'auto', fontSize: '0.85em' }}>{
`curl -X POST ${process.env.REACT_APP_API_ENDPOINT}/service-account-credentials \\
  -H "x-api-key: YOUR_API_KEY"`
                        }</pre>
                        <p style={{ margin: '8px 0', color: '#666' }}>
                            This returns temporary credentials including <code>access_key_id</code>, <code>secret_access_key</code>, <code>session_token</code>, plus the <code>s3_bucket</code> and <code>s3_prefix</code> for your account. Credentials are valid for 12 hours.
                        </p>

                        <p style={{ margin: '16px 0 8px 0' }}><strong>Step 2:</strong> Upload files using the temporary credentials</p>
                        <pre style={{ background: '#1e1e1e', color: '#d4d4d4', padding: '12px', borderRadius: '4px', overflowX: 'auto', fontSize: '0.85em' }}>{
`AWS_ACCESS_KEY_ID="<access_key_id>" \\
AWS_SECRET_ACCESS_KEY="<secret_access_key>" \\
AWS_SESSION_TOKEN="<session_token>" \\
aws s3 cp ./my-file.dd \\
  s3://<s3_bucket>/<s3_prefix>my-file.dd \\
  --region ${process.env.REACT_APP_AWS_REGION || 'us-west-2'}`
                        }</pre>

                        <p style={{ margin: '16px 0 8px 0' }}><strong>Long-running scripts:</strong> Refresh credentials before they expire</p>
                        <pre style={{ background: '#1e1e1e', color: '#d4d4d4', padding: '12px', borderRadius: '4px', overflowX: 'auto', fontSize: '0.85em' }}>{
`#!/bin/bash
# Refresh credentials every 11 hours (they expire after 12)
API_KEY="YOUR_API_KEY"
API_URL="${process.env.REACT_APP_API_ENDPOINT}/service-account-credentials"

refresh_credentials() {
  CREDS=$(curl -s -X POST "$API_URL" -H "x-api-key: $API_KEY")
  export AWS_ACCESS_KEY_ID=$(echo $CREDS | jq -r .access_key_id)
  export AWS_SECRET_ACCESS_KEY=$(echo $CREDS | jq -r .secret_access_key)
  export AWS_SESSION_TOKEN=$(echo $CREDS | jq -r .session_token)
  export S3_BUCKET=$(echo $CREDS | jq -r .s3_bucket)
  export S3_PREFIX=$(echo $CREDS | jq -r .s3_prefix)
}

refresh_credentials  # Initial fetch
# Upload files, refreshing every 11 hours as needed
aws s3 cp ./my-file.dd "s3://$S3_BUCKET/$\{S3_PREFIX}my-file.dd"`
                        }</pre>

                        <div style={{ marginTop: '12px', padding: '10px', background: '#e8f4fd', borderRadius: '4px', borderLeft: '3px solid #0066cc' }}>
                            <strong>Notes:</strong>
                            <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
                                <li>Credentials expire after 12 hours. For long-running processes, refresh before expiry using the script pattern above.</li>
                                <li>Uploads are restricted to the S3 prefix configured for the service account.</li>
                                <li>Only <code>s3:PutObject</code> and <code>s3:PutObjectAcl</code> actions are permitted.</li>
                            </ul>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ManageCredentials;
